//theme type, pocketPickTier type, default

import React, { useEffect, useRef, useState } from 'react'
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import Animated, {
  FadeIn,
  FadeInUp,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSpring,
  withTiming
} from 'react-native-reanimated'
import { router } from 'expo-router'
import { Suit, Value } from 'pokersolver'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { strengthTier, handStrengthScore } from '@ppwr/poker-engine/utils'

import { DS } from '@/design-system'
import { WebWrapper } from '@/components/WebWrapper'
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'
import { triggerHaptic } from '@/screens/new-design/utils/triggerHaptics/triggerHaptics'

//import { type PocketPickTier } from '@/screens/pocketPick/pocketPickTheme'
import { type ThemeTier } from '@/components/Mini/theme'
import { defaultTheme } from '@/components/Mini/theme'

import * as pocketPick from '@/router/(root)/pocketPick/pocketPickModule'
import { PocketPickBackground as Background } from "@/components/Mini/Background"
import { PocketPickStatusBar as StatusBar } from "@/components/Mini/StatusBar"
import { PocketPickHeader as Header } from "@/components/Mini/Header"
import { PrimaryBtn, SecondaryBtn } from "@/components/Mini/Button"


const { MIN_SELECTION_MS, ROUND_COUNT } = GAME_CONSTANTS.POCKET_PICK_CONSTANTS

const SHOWN_SIZE = 132
const FAN_SIZE = 92

type Reveal = {
  correct: boolean
  bestCandidateIndex: number
  candidateEquities: number[]
  candidateExplanations: string[]
  bestTier: ThemeTier
  scored: boolean
  gameOver?: boolean
}

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms))

export const PocketPickGame = () => {
  const playedDate = pocketPick.useStore((s) => s.playedDate)
  const { userId, userName } = pocketPick.usePocketPickUser()
  
  const handleClose = pocketPick.useHandleClose()

  const theme = pocketPick.useStore(s => s.theme)

  const start = useMutation(api.pocketPick.mutations.startPocketPickGame)
  const submit = useMutation(api.pocketPick.mutations.submitPocketPickSelection)

  const status = useQuery(api.pocketPick.queries.getPocketPickStatus, userId ? { userId, playedDate } : 'skip')

  const [ready, setReady] = useState(false)
  const [startupError, setStartupError] = useState<string | null>(null)
  //const [startupError, setStartupError] = useState<string | null>('Test error message')
  const [localRound, setLocalRound] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [reveal, setReveal] = useState<Reveal | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const roundStart = useRef<number>(Date.now())

  const roundData = useQuery(
    api.pocketPick.queries.getPocketPickRound,
    userId && ready && localRound !== null ? { userId, playedDate, roundIndex: localRound } : 'skip'
  )

  // Snapshot of the round's cards so the reveal survives the live query going stale.
  const [snapshot, setSnapshot] = useState<{ shownCard: string; candidateCards: string[]; totalRounds: number } | null>(
    null
  )

  // Start (or resume/replay) once.
  useEffect(() => {
    if (!userId) return
    start({ userId, playedDate, todayDate: playedDate })
      .then(() => setReady(true))
      .catch((e) => setStartupError(e instanceof Error ? e.message : 'Unable to start the game.'))
  }, [userId, playedDate, start])

  // Initialise the round pointer from server state once.
  useEffect(() => {
    if (ready && localRound === null && status) {
      setLocalRound(status.status === 'in_progress' ? status.roundIndex : 0)
    }
  }, [ready, status, localRound])

  // Capture each new round's cards and reset interaction.
  useEffect(() => {
    if (roundData && (!snapshot || roundData.shownCard !== snapshot.shownCard)) {
      setSnapshot({
        shownCard: roundData.shownCard,
        candidateCards: roundData.candidateCards,
        totalRounds: roundData.totalRounds
      })
      setSelectedIndex(null)
      setReveal(null)
      roundStart.current = Date.now()
    }
  }, [roundData, snapshot])

  // Safety net: if the round query resolves to null (e.g. advanced past the last
  // round) with nothing to show, go to results rather than dead-ending on "Dealing…".
  useEffect(() => {
    if (ready && localRound !== null && roundData === null && !snapshot && !reveal) {
      router.replace('/(root)/pocketPick/results')
    }
  }, [ready, localRound, roundData, snapshot, reveal])

  // Recover from a round desync (e.g. the response was lost after the server
  // scored): once a submit error surfaces, follow the server's round pointer.
  // A desync past the final round completes the game server-side, so a retry
  // there succeeds via the unscored replay path — no special case needed.
  useEffect(() => {
    if (!submitError || localRound === null) return
    if (status?.status === 'in_progress' && status.roundIndex !== localRound) {
      setLocalRound(status.roundIndex)
      setSnapshot(null)
      setSelectedIndex(null)
      setSubmitError(null)
    }
  }, [submitError, status, localRound])

  const onPick = (index: number) => {
    if (reveal || submitting) return
    triggerHaptic()
    setSelectedIndex(index)
    setSubmitError(null)
    
  }

  const onLockIn = async () => {
    if (selectedIndex === null || reveal || submitting || localRound === null) return
    setSubmitting(true)
    setSubmitError(null)
    const elapsed = Date.now() - roundStart.current
    if (elapsed < MIN_SELECTION_MS) {
      await sleep(MIN_SELECTION_MS - elapsed)
    }
    try {
      const res = (await submit({
        userId: userId as string,
        userName: userName ?? undefined,
        playedDate,
        roundIndex: localRound,
        selectedIndex,
        elapsedMs: Date.now() - roundStart.current
      })) as Reveal
      //throw new Error("test submit failure")
      setReveal(res)
      triggerHaptic()
    } catch (e) {
      // Keep the selection so the user can retry; surface what went wrong.
      //setSubmitError(e instanceof Error ? e.message : 'Something went wrong. Try again.')
      // Keep the selection so the user can retry; give user generic error message.
      setSubmitError('Something went wrong. Try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const onNext = () => {
    if (!reveal || localRound === null || !snapshot) return
    if (reveal.gameOver) {
      router.replace('/(root)/pocketPick/results')
      return
    }
    setLocalRound(localRound + 1)
    setSnapshot(null)
    setReveal(null)
    setSelectedIndex(null)
  }

  const totalRounds = snapshot?.totalRounds ?? ROUND_COUNT

  const showStartUpError = startupError;


  let showDealing = false;
  if (!startupError){
    if (!snapshot || localRound === null){
      showDealing = true;
    } 
  }

  let showGame = false;
  if (!startupError && !showDealing){
    showGame = true;
  }

  const textColor = theme.textDefault 
  const cardTilt = { left: -5, right: 5 }
  
  return (
    <Background>

            {showStartUpError &&
            <View style={[styles.fill, styles.center , styles.startupError]}>
            <DS.Text variant="Body Large/Regular" style={{color: textColor}}>
              {startupError}
            </DS.Text>
            <BackBtn />
            </View>
            }

            {showDealing &&
            <View style={[styles.fill, styles.center , styles.startupError]}>
              <DS.Text variant="Body Large/Regular" style={[{color: textColor}]}>
                Dealing…
              </DS.Text>
              </View>
            }

            {showGame && snapshot && localRound !== null &&
            <View style={{flex:1}}>  
            <Header
              title={`Hand ${Math.min(localRound + 1, totalRounds)} / ${totalRounds}`}
              handler={handleClose}
            />
            <View style={{flex:1}}> 
            {/* Top: the two-card hand laid on the table */}
            <View style={[styles.table, { zIndex: 1000 }]}>
              <View style={[styles.tableCard, { transform: [{ rotate: `${cardTilt.left}deg` }] }]}>
                <CardFace card={snapshot.shownCard} size={SHOWN_SIZE} />
              </View>
              <View style={[styles.tableCardRight, { transform: [{ rotate: `${cardTilt.right}deg` }] }]}>
                {selectedIndex !== null ? <PlacedCard card={snapshot.candidateCards[selectedIndex]} /> : <EmptySlot />}
              </View>
            </View>

            {/* Middle: prompt / lock-in / reveal banner */}
            {/* flex: 1, alignItems: 'center', justifyContent: 'center'*/}
            <View style={[{flex: 1, alignItems: 'center', justifyContent: 'center'},{ position: "relative", zIndex: 3000 }]}>
            <View style={[{flex: 1},{ position: "absolute", paddingHorizontal: 24, gap: 12  }]}>
              {!reveal && selectedIndex === null ? (
                <DS.Text variant="Body Large/Regular" style={{color: textColor}}>
                  Pick the best second card
                </DS.Text>
              ) : null}

              {!reveal && selectedIndex !== null ? (
                <LockInBtn handler={onLockIn} submitting={submitting} />
              ) : null}
            
              {submitError && !reveal ? (
                <DS.Text variant="Body Large/Regular" style={[styles.errorText, {color: theme.danger}]}>
                  {submitError}
                </DS.Text>
              ) : null}

              {reveal ? (
                <Animated.View
                  entering={FadeIn}
                  style={[
                    styles.revealBanner,
                    //Platform.OS === 'web'
                      //? 
                      {
                        backgroundColor: 'white',
                        borderRadius: 16,
                        opacity: 0.9,
                        padding: 16,
                        margin: 8
                      }
                      //: {}
                  ]}
                >
                  <DS.Text
                    variant="Heading 3/Medium"
                    style={{ color: reveal.correct ? theme.tier.premium.color : theme.tier.muck.color }}
                  >
                    {reveal.correct ? 'Best pick' : 'Not the best'}
                  </DS.Text>
                  <DS.Text
                    variant="Body Large/Regular"
                    style={[styles.explanation, {color: DS.palette['Global/Black']}/*{color: Platform.OS === 'web' ? DS.palette['Global/Black']: theme.textDefault}*/]}
                  >
                    {reveal.candidateExplanations[reveal.bestCandidateIndex]}
                  </DS.Text>
                </Animated.View>
              ) : null}
            </View>
            </View>

            {/* Bottom: the three candidates, lightly fanned in-hand */}
            <View style={[styles.fanRow, { zIndex: 2000 }]}>
              {snapshot.candidateCards.map((card, i) => {
                const fanAngle = (i - 1) * 8
                const offsetY = i === 1 ? -10 : 0 // raise the middle card into an arc
                // Echo the card arc under the row: sides drop down and pull toward center.
                const strengthShiftX = i === 0 ? 12 : i === 2 ? -12 : 0
                const strengthShiftY = i === 1 ? 0 : 8
                const isBest = reveal?.bestCandidateIndex === i
                const isPicked = selectedIndex === i
                return (
                  <FanCard
                    key={card}
                    card={card}
                    angle={fanAngle}
                    offsetY={offsetY}
                    z={i}
                    dimmed={isPicked && !reveal}
                    isBest={!!reveal && isBest}
                    onPress={() => onPick(i)}
                    disabled={!!reveal || submitting}
                    equity={reveal ? reveal.candidateEquities[i] : null}
                    strengthShiftX={strengthShiftX}
                    strengthShiftY={strengthShiftY}
                    theme={theme}
                  />
                )
              })}
            </View>

            {reveal ? (
              <DS.Text variant="Body Small/Regular" style={[styles.strengthCaption, {color: theme.textDefaultDim} ]}>
                Starting hand strength score
              </DS.Text>
            ) : null}

            {reveal ? (
              <View style={styles.footer}>
                {reveal.gameOver &&
                  <ResultsBtn handler={onNext} />
                }
                {!reveal.gameOver &&
                  <NextBtn handler={onNext} />
                }
              </View>
            ) : null}
            </View>
            </View>
          }

    </Background>
  )
}

function NextBtn({ handler }: { handler: () => void }) {

  const theme = pocketPick.useStore(s => s.theme)
  const label = "Next hand"

  return (
    <PrimaryBtn theme={theme} handler={handler} label={label} />
  )
   
}

function ResultsBtn({ handler }: { handler: () => void }) {

  const theme = pocketPick.useStore(s => s.theme)
  const label = "See results"

  return (
    <PrimaryBtn theme={theme} handler={handler} label={label} />
  )

}

function BackBtn(){

  const theme = pocketPick.useStore(s => s.theme)
  const handleClose = pocketPick.useHandleClose()
  const label = "Back"

  return (
    <PrimaryBtn theme={theme} handler={handleClose} label={label} />
  )

}

 function LockInBtn({ handler, submitting }: { handler: () => void; submitting: boolean }) {

  const theme = pocketPick.useStore(s => s.theme)
  const label = submitting ? 'Locking in…' : 'Lock in'
  const isDisabled = submitting

  return (
    <PrimaryBtn theme={theme} handler={handler} label={label} isDisabled={isDisabled} />
  )
}

const CardFace = ({ card, size }: { card: string; size: number }) => (
  <PokerCard.Front value={card[0] as Value} suit={card[1] as Suit} size={size} shadow />
)

function EmptySlot(){
  const theme = pocketPick.useStore(s => s.theme)

  return (
    <View style={[
      styles.slot, 
      { 
        width: SHOWN_SIZE, 
        height: Math.round(SHOWN_SIZE * 1.47), 
        borderColor: theme.slotEdge,
        backgroundColor: theme.slotSurface
      }
    ]}>
      <DS.Text variant="Heading 1/Medium" style={{color: theme.slotQ}}>
        ?
      </DS.Text>
    </View>
  )

}


const PlacedCard = ({ card }: { card: string }) => {
  const scale = useSharedValue(0.6)
  useEffect(() => {
    scale.value = withSpring(1, { damping: 10, stiffness: 140 })
  }, [card, scale])
  const style = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }))
  return (
    <Animated.View style={style}>
      <CardFace card={card} size={SHOWN_SIZE} />
    </Animated.View>
  )
}

const FanCard = ({
  card,
  angle,
  offsetY,
  z,
  dimmed,
  isBest,
  onPress,
  disabled,
  equity,
  strengthShiftX,
  strengthShiftY,
  theme
}: {
  card: string
  angle: number
  offsetY: number
  z: number
  dimmed: boolean
  isBest: boolean
  onPress: () => void
  disabled: boolean
  equity: number | null
  strengthShiftX: number
  strengthShiftY: number
  theme: typeof defaultTheme
}) => {

  const glow = useSharedValue(0)
  useEffect(() => {
    if (isBest) {
      glow.value = withRepeat(withTiming(1, { duration: 900 }), -1, true)
    }
  }, [isBest, glow])

  const glowStyle = useAnimatedStyle(() => ({
    opacity: isBest ? 0.35 + glow.value * 0.45 : 0,
    shadowOpacity: isBest ? 0.4 + glow.value * 0.5 : 0
  }))

  const fanTiltBack = 8

  return (
    <View style={[styles.fanItem, { zIndex: z, elevation: z }]}>
      <TouchableOpacity
        onPress={onPress}
        disabled={disabled}
        activeOpacity={0.9}
        style={{
          // Pre-reveal: fade the picked card. On reveal: fade the non-best cards so
          // the correct pick stands out (mirrors CTH's faded non-answer cards).
          opacity: dimmed ? 0.45 : equity !== null && !isBest ? 0.3 : 1,
          transform: [
            { perspective: 600 },
            { translateY: offsetY },
            { rotateX: `${fanTiltBack}deg` },
            { rotate: `${angle}deg` }
          ]
        }}
      >
        <Animated.View style={[styles.glowRing, glowStyle, {borderColor: theme.bestGlow, shadowColor: theme.bestGlow}]} />
        <PokerCard.Front value={card[0] as Value} suit={card[1] as Suit} size={FAN_SIZE} shadow />
      </TouchableOpacity>
      {equity !== null ? (
        <View style={{ transform: [{ translateX: strengthShiftX }, { translateY: strengthShiftY }] }}>
          <StrengthCounter value={handStrengthScore(equity)} color={theme.tier[strengthTier(equity)].color} />
        </View>
      ) : null}
    </View>
  )
}

const StrengthCounter = ({ value, color }: { value: number; color: string }) => {
  const [shown, setShown] = useState(0)
  useEffect(() => {
    let frame = 0
    const steps = 24
    const id = setInterval(() => {
      frame++
      setShown(Math.round((value * frame) / steps))
      if (frame >= steps) {
        setShown(Math.round(value))
        clearInterval(id)
      }
    }, 18)
    return () => clearInterval(id)
  }, [value])
  return (
    <Animated.View entering={FadeInUp}>
      <DS.Text variant="Heading 3/Medium" style={{ color }}>
        {shown}
      </DS.Text>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
  center: { alignItems: 'center', justifyContent: 'center' },
  startupError: { gap: 24, paddingHorizontal: 24 },
  table: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 25
  },
  tableCard: { marginRight: -20, zIndex: 2 },
  tableCardRight: { marginLeft: -20, zIndex: 1 },
  slot: {
    borderRadius: 10,
    borderWidth: 2,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center'
  },
  middle: { flex: 1, alignItems: 'center', justifyContent: 'center'},
  revealBanner: { alignItems: 'center', gap: 10 },
  errorText: { textAlign: 'center' },
  explanation: { textAlign: 'center' },
  strengthCaption: { textAlign: 'center', marginTop: -42, marginBottom: 22 },
  fanRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingTop: 28,
    marginBottom: 54
  },
  fanItem: { alignItems: 'center', gap: 8, marginHorizontal: -2 },
  glowRing: {
    position: 'absolute',
    top: -8,
    left: -8,
    right: -8,
    bottom: -8,
    borderRadius: 14,
    borderWidth: 4,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 0 }
  },
  footer: { paddingHorizontal: 24, paddingBottom: 16 }
})

